import { http } from '@/api/http';
import { ApiResponseBody, PaginatedApiResponseBody, PaginationSearchParams } from '@/api/types';
import {
  BenchmarkData,
  SetBenchmarkBody,
  RecommendedFlightsParams,
  RecommendedFlightType,
  RouteBenchmarkResponse,
  HereMapsTokenType,
  SuggestedRouteMetricsType,
  // Route Service V2 types
  LogisticsProjectionType,
  AddLegParams,
  UpdateLegParams,
  LegIdentifiers,
  HubIdentifiers,
  RouteIdentifiers,
  AddTmdxJetAviaLeg,
  AddSupplementalLiftAviaLeg,
  AddTxCenterProviderAviaLeg,
  AddTmdxAffiliateGroundLeg,
  AddTxCenterProviderGroundLeg,
  AddCurriServiceGroundLeg,
  AddTmdxDriveTedsGroundLeg,
  AddTmdxDriveAffiliateGroundLeg,
  AddSelfServicedGroundLeg,
  AddAccommodationLeg,
  AddCommercialAviaLeg,
  LogisticsLogProjectionType,
  AddSabreCommercialAviaLegs,
  ModifySabreCommercialAviaLegs,
  SuggestedRoutesParams,
  LogisticsVersionIdentifier,
  ResourceIdentifiers,
  RemoveRouteLegsParams,
  GetLogisticsListParams,
  LogisticsProjectionResponse,
  // Device Tracking types
  RouteDeviceTracking,
  GetDeviceTrackingListParams,
  PatchDisplaySettingsRequestBody,
  DeviceTrackingLogType,
  // Aircraft utilization
  AircraftUtilizationRaw,
  AircraftUtilizationParams,
} from './route.types';
import { getRouteApiUrl } from './route.paths';

const getBenchmarkById = async (benchmarkId: string) => {
  const res = await http.get<ApiResponseBody<RouteBenchmarkResponse>>(getRouteApiUrl.getBenchmarkById(benchmarkId));
  return res.data.data;
};

const setBenchmark = async (data: SetBenchmarkBody) => {
  const res = await http.post<ApiResponseBody<BenchmarkData>>(getRouteApiUrl.setBenchmark(), data);
  return res.data.data;
};

const getRecommendedFlights = async (params: RecommendedFlightsParams) => {
  const res = await http.get<ApiResponseBody<{ flights: RecommendedFlightType[] }>>(
    getRouteApiUrl.recommendedFlights(),
    {
      params,
    },
  );
  return res.data.data;
};

const createHereMapsToken = async () => {
  const res = await http.post<ApiResponseBody<HereMapsTokenType>>(getRouteApiUrl.createHereMapsToken());

  return res.data.data;
};

const sendSuggestedRoutesMetrics = async (data: SuggestedRouteMetricsType) => {
  const res = await http.post<ApiResponseBody<undefined>>(getRouteApiUrl.suggestedRoutesMetrics(), data);
  return res.data;
};

const getAircraftUtilization = async (params: PaginationSearchParams & AircraftUtilizationParams) => {
  const res = await http.get<PaginatedApiResponseBody<AircraftUtilizationRaw>>(getRouteApiUrl.aircraftUtilization(), {
    params,
  });
  return res.data.data;
};

// Route Service V2 Fetchers
const getLogisticsList = async (params: PaginationSearchParams & Partial<GetLogisticsListParams>) => {
  const res = await http.get<PaginatedApiResponseBody<LogisticsProjectionType>>(getRouteApiUrl.getLogisticsList(), {
    params,
    paramsSerializer: { indexes: null },
  });
  return res.data.data;
};

const createLogistics = async ({ caseId }: { caseId: string }) => {
  const res = await http.post<ApiResponseBody<LogisticsProjectionType>>(getRouteApiUrl.createLogistics(caseId));
  return res.data.data;
};

const getLogisticsById = async (logisticsId: string): Promise<LogisticsProjectionResponse> => {
  const res = await http.get<ApiResponseBody<LogisticsProjectionResponse>>(
    getRouteApiUrl.getLogisticsById(logisticsId),
  );
  return res.data.data;
};

const getLogisticsSnapshotByVersion = async (
  logisticsId: string,
  version: number,
): Promise<LogisticsProjectionResponse> => {
  const res = await http.get<ApiResponseBody<LogisticsProjectionResponse>>(
    getRouteApiUrl.getLogisticsSnapshotByVersion(logisticsId, `${version}`),
  );
  return res.data.data;
};

// Suggested Routes
const addSuggestedRoutes = async ({ logisticsId, ...data }: SuggestedRoutesParams) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addSuggestedRoutes(logisticsId), data);
  return res.data.data;
};

const addPartialSuggestedRoutes = async ({ logisticsId, ...data }: SuggestedRoutesParams) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addPartialSuggestedRoutes(logisticsId), data);
  return res.data.data;
};

// Hubs
const setDepartureHub = async ({
  logisticsId,
  routeId,
  hubId,
  version,
}: HubIdentifiers & LogisticsVersionIdentifier) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.assignDepartureHub(logisticsId, routeId, hubId), {
    version,
  });
  return res.data;
};

const removeDepartureHub = async ({ logisticsId, routeId, version }: RouteIdentifiers & LogisticsVersionIdentifier) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.unassignDepartureHub(logisticsId, routeId), {
    version,
  });
  return res.data;
};

const setArrivalHub = async ({ logisticsId, routeId, hubId, version }: HubIdentifiers & LogisticsVersionIdentifier) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.assignArrivalHub(logisticsId, routeId, hubId), {
    version,
  });
  return res.data;
};

const removeArrivalHub = async ({ logisticsId, routeId, version }: RouteIdentifiers & LogisticsVersionIdentifier) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.unassignArrivalHub(logisticsId, routeId), {
    version,
  });
  return res.data;
};

// TMDX Avia Leg
const addTmdxAviaLeg = async ({ logisticsId, routeId, data, version }: AddLegParams<AddTmdxJetAviaLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addTmdxAviaLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateTmdxAviaLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddTmdxJetAviaLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(getRouteApiUrl.updateTmdxAviaLeg(logisticsId, routeId, legId), {
    version,
    ...data,
  });
  return res.data;
};

// Supplemental Lift Avia Leg
const addSupplementalLiftLeg = async ({
  logisticsId,
  routeId,
  data,
  version,
}: AddLegParams<AddSupplementalLiftAviaLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addSupplementalLiftLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateSupplementalLiftLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddSupplementalLiftAviaLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateSupplementalLiftLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

// TX Center Avia Leg
const addTxCenterAviaLeg = async ({
  logisticsId,
  routeId,
  data,
  version,
}: AddLegParams<AddTxCenterProviderAviaLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addTxCenterAviaLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateTxCenterAviaLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddTxCenterProviderAviaLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateTxCenterAviaLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

// Commercial Flight Leg
const addCommercialLeg = async ({ logisticsId, routeId, data, version }: AddLegParams<AddCommercialAviaLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addCommercialLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const addSabreCommercialLegs = async ({
  logisticsId,
  routeId,
  data,
  version,
}: AddLegParams<AddSabreCommercialAviaLegs>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addSabreCommercialLegs(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateCommercialLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddCommercialAviaLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(getRouteApiUrl.updateCommercialLeg(logisticsId, routeId, legId), {
    version,
    ...data,
  });
  return res.data;
};

const updateSabreCommercialLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<ModifySabreCommercialAviaLegs>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateSabreCommercialLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

const removeSabreCommercialLegs = async ({
  logisticsId,
  routeId,
  sabreGroupId,
  version,
}: RouteIdentifiers & LogisticsVersionIdentifier & { sabreGroupId: string }) => {
  const res = await http.delete<ApiResponseBody<void>>(
    getRouteApiUrl.removeSabreCommercialLegs(logisticsId, routeId, sabreGroupId),
    { data: { version } },
  );
  return res.data;
};

// TMDX Affiliate Ground Leg
const addTmdxAffiliateLeg = async ({
  logisticsId,
  routeId,
  data,
  version,
}: AddLegParams<AddTmdxAffiliateGroundLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addTmdxAffiliateLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateTmdxAffiliateLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddTmdxAffiliateGroundLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateTmdxAffiliateLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

// TMDX Drive Affiliate Ground Leg
const addTmdxDriveAffiliateLeg = async ({
  logisticsId,
  routeId,
  data,
  version,
}: AddLegParams<AddTmdxDriveAffiliateGroundLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addTmdxDriveAffiliateLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateTmdxDriveAffiliateLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddTmdxDriveAffiliateGroundLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateTmdxDriveAffiliateLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

// TX Center Ground Leg
const addTxCenterGroundLeg = async ({
  logisticsId,
  routeId,
  data,
  version,
}: AddLegParams<AddTxCenterProviderGroundLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addTxCenterGroundLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateTxCenterGroundLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddTxCenterProviderGroundLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateTxCenterGroundLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

// Curri Service Ground Leg
const addCurriServiceLeg = async ({ logisticsId, routeId, data, version }: AddLegParams<AddCurriServiceGroundLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addCurriServiceLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateCurriServiceLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddCurriServiceGroundLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateCurriServiceLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

// TMDX Drive Affiliate Ground Leg
const addTmdxDriveTedsLeg = async ({
  logisticsId,
  routeId,
  data,
  version,
}: AddLegParams<AddTmdxDriveTedsGroundLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addTmdxDriveTedsGroundLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateTmdxDriveTedsLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddTmdxDriveTedsGroundLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateTmdxDriveTedsGroundLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

// Self Serviced Ground Leg
const addSelfServicedGroundLeg = async ({
  logisticsId,
  routeId,
  data,
  version,
}: AddLegParams<AddSelfServicedGroundLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addSelfServicedGroundLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateSelfServicedGroundLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddSelfServicedGroundLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateSelfServicedGroundLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

// Accommodation Leg
const addAccommodationLeg = async ({ logisticsId, routeId, data, version }: AddLegParams<AddAccommodationLeg>) => {
  const res = await http.post<ApiResponseBody<void>>(getRouteApiUrl.addAccommodationLeg(logisticsId, routeId), {
    version,
    ...data,
  });
  return res.data;
};

const updateAccommodationLeg = async ({
  logisticsId,
  routeId,
  legId,
  data,
  version,
}: UpdateLegParams<AddAccommodationLeg>) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.updateAccommodationLeg(logisticsId, routeId, legId),
    {
      version,
      ...data,
    },
  );
  return res.data;
};

// Remove Leg
const removeLeg = async ({ logisticsId, routeId, legId, version }: LegIdentifiers & LogisticsVersionIdentifier) => {
  const res = await http.delete<ApiResponseBody<void>>(getRouteApiUrl.removeLeg(logisticsId, routeId, legId), {
    data: { version },
  });
  return res.data;
};

// Remove route
const removeRoute = async ({ logisticsId, routeId, version, legIds }: RemoveRouteLegsParams) => {
  const res = await http.delete<ApiResponseBody<void>>(getRouteApiUrl.removeRoute(logisticsId, routeId), {
    data: {
      version,
      legIds,
    },
  });
  return res.data;
};

// Remove resource
const removeResource = async ({
  logisticsId,
  resourceId,
  version,
}: ResourceIdentifiers & LogisticsVersionIdentifier) => {
  const res = await http.delete<ApiResponseBody<void>>(getRouteApiUrl.removeResource(logisticsId, resourceId), {
    data: { version },
  });
  return res.data;
};

// Logistics Logs
const getLogisticsLogs = async (logisticsId: string): Promise<LogisticsLogProjectionType> => {
  const res = await http.get<ApiResponseBody<LogisticsLogProjectionType>>(getRouteApiUrl.getLogisticsLogs(logisticsId));

  return res.data.data;
};

// Device Tracking Fetchers
const getDeviceTrackingList = async (params: GetDeviceTrackingListParams) => {
  const res = await http.get<PaginatedApiResponseBody<RouteDeviceTracking>>(getRouteApiUrl.getDeviceTrackingList(), {
    params,
    paramsSerializer: { indexes: null },
  });
  return res.data.data;
};

const getDeviceTrackingById = async (deviceTrackingId: string) => {
  const res = await http.get<ApiResponseBody<RouteDeviceTracking>>(
    getRouteApiUrl.getDeviceTrackingById(deviceTrackingId),
  );
  return res.data.data;
};

const getDeviceTrackingByCaseId = async (caseId: string) => {
  const res = await http.get<ApiResponseBody<RouteDeviceTracking>>(getRouteApiUrl.getDeviceTrackingByCaseId(caseId));
  return res.data.data;
};

const patchDeviceTrackingDisplaySettings = async (deviceTrackingId: string, data: PatchDisplaySettingsRequestBody) => {
  const res = await http.patch<ApiResponseBody<void>>(
    getRouteApiUrl.patchDeviceTrackingDisplaySettings(deviceTrackingId),
    data,
  );
  return res.data;
};

const getDeviceTrackingLogs = async (deviceTrackingId: string, params: PaginationSearchParams) => {
  const res = await http.get<PaginatedApiResponseBody<DeviceTrackingLogType>>(
    getRouteApiUrl.getDeviceTrackingLogs(deviceTrackingId),
    { params },
  );
  return res.data.data;
};

export const routeApiFetcher = {
  setBenchmark,
  getRecommendedFlights,
  getBenchmarkById,
  createHereMapsToken,
  sendSuggestedRoutesMetrics,
  getAircraftUtilization,
  // Route Service V2
  getLogisticsList,
  createLogistics,
  getLogisticsById,
  getLogisticsSnapshotByVersion,
  addSuggestedRoutes,
  addPartialSuggestedRoutes,
  addTmdxAviaLeg,
  updateTmdxAviaLeg,
  addSupplementalLiftLeg,
  updateSupplementalLiftLeg,
  addTxCenterAviaLeg,
  updateTxCenterAviaLeg,
  addCommercialLeg,
  addSabreCommercialLegs,
  updateSabreCommercialLeg,
  updateCommercialLeg,
  removeSabreCommercialLegs,
  addTmdxAffiliateLeg,
  updateTmdxAffiliateLeg,
  addTmdxDriveAffiliateLeg,
  updateTmdxDriveAffiliateLeg,
  addTxCenterGroundLeg,
  updateTxCenterGroundLeg,
  addCurriServiceLeg,
  updateCurriServiceLeg,
  addTmdxDriveTedsLeg,
  updateTmdxDriveTedsLeg,
  addSelfServicedGroundLeg,
  updateSelfServicedGroundLeg,
  addAccommodationLeg,
  updateAccommodationLeg,
  removeLeg,
  removeRoute,
  setDepartureHub,
  removeDepartureHub,
  setArrivalHub,
  removeArrivalHub,
  removeResource,
  getLogisticsLogs,
  // Device Tracking
  getDeviceTrackingList,
  getDeviceTrackingById,
  getDeviceTrackingByCaseId,
  patchDeviceTrackingDisplaySettings,
  getDeviceTrackingLogs,
};
